/**
 * Encrypted localStorage utility
 * Handles encryption/decryption of data stored in localStorage
 */
import CryptoJS from 'crypto-js';

const decryptWarningCache = new Set();

export const getAppSecretKey = () =>
  process.env.REACT_APP_SECRETKEY || process.env.REACT_APP_STORAGE_KEY || '';

const getSecretKey = () => getAppSecretKey();

const logDecryptFailureOnce = (key, error) => {
  if (decryptWarningCache.has(key)) return;
  decryptWarningCache.add(key);
  console.warn(`Failed to read encrypted ${key}. Clearing stale value.`, error);
};

const buildMetadataFromLegacyStorage = () => {
  try {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const rawUser = localStorage.getItem('user');
    if (!token && !rawUser) return null;

    let userData = null;
    if (rawUser) {
      try {
        userData = JSON.parse(rawUser);
      } catch {
        userData = null;
      }
    }

    if (!userData && !token) return null;

    return {
      uid: userData?.uid || userData?.id || null,
      email: userData?.email || '',
      role_id: userData?.role_id || userData?.roleId || null,
      phone_no: userData?.phone_no || userData?.phone || '',
      name: userData?.name || '',
      designation: userData?.designation || null,
      profile: userData?.profile || null,
      cid: userData?.cid || null,
      created_at: userData?.created_at || null,
      created_by: userData?.created_by || null,
      status: userData?.status ?? true,
      notification: userData?.notification || 'false',
      candidate_id: userData?.candidate_id || null,
      refresh_token: userData?.refresh_token || null,
      passcode: userData?.passcode || null,
      subscription_id: userData?.subscription_id || null,
      fitment_level: userData?.fitment_level || 'RELAXED',
      company_name: userData?.company_name || null,
      plan_id: userData?.plan_id || null,
      token: token || null,
    };
  } catch {
    return null;
  }
};

/**
 * Encrypt and store data in localStorage
 * @param {string} key - localStorage key
 * @param {any} data - Data to encrypt and store
 */
export const setEncryptedItem = (key, data) => {
  try {
    const secretKey = getSecretKey();
    if (!secretKey) {
      console.error(`Missing encryption key while storing ${key}`);
      return false;
    }
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, secretKey).toString();
    localStorage.setItem(key, encrypted);
    return true;
  } catch (error) {
    console.error(`Failed to encrypt and store ${key}:`, error);
    return false;
  }
};

/**
 * Get and decrypt data from localStorage
 * @param {string} key - localStorage key
 * @returns {any} Decrypted data or null if not found/failed
 */
export const getEncryptedItem = (key) => {
  try {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    
    // Check if data is already in plain JSON format (backward compatibility)
    try {
      const parsed = JSON.parse(encrypted);
      // If parse succeeds, it's plain JSON - return it and optionally re-encrypt
      console.warn(`${key} was stored in plain format. Consider re-encrypting.`);
      return parsed;
    } catch {
      // Not plain JSON, try to decrypt
    }
    
    const secretKey = getSecretKey();
    if (!secretKey) {
      console.error(`Missing encryption key while reading ${key}`);
      return null;
    }

    const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decrypted) {
      throw new Error('Empty decrypted value');
    }
    
    return JSON.parse(decrypted);
  } catch (error) {
    if (key === 'metadata') {
      logDecryptFailureOnce(key, error);

      const recoveredMetadata = buildMetadataFromLegacyStorage();
      if (recoveredMetadata) {
        setEncryptedItem('metadata', recoveredMetadata);
        return recoveredMetadata;
      }

      try {
        localStorage.removeItem('metadata');
      } catch {
        // Ignore cleanup errors.
      }
      return null;
    }

    console.error(`Failed to get and decrypt ${key}:`, error);
    return null;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - localStorage key
 */
export const removeEncryptedItem = (key) => {
  localStorage.removeItem(key);
};

/**
 * Get metadata (user info) from encrypted localStorage
 * @returns {Object|null} User metadata or null
 */
export const getMetadata = () => {
  return getEncryptedItem('metadata');
};

export const getAuthToken = () => {
  try {
    const metadata = getMetadata();
    if (metadata?.token) return metadata.token;
  } catch {
    // Ignore and fall back to plain localStorage token.
  }

  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

/**
 * Set metadata (user info) in encrypted localStorage
 * @param {Object} metadata - User metadata to store
 * @returns {boolean} Success status
 */
export const setMetadata = (metadata) => {
  return setEncryptedItem('metadata', metadata);
};

/**
 * Update specific fields in metadata
 * @param {Object} updates - Fields to update
 * @returns {boolean} Success status
 */
export const updateMetadata = (updates) => {
  try {
    const current = getMetadata() || {};
    const updated = { ...current, ...updates };
    return setMetadata(updated);
  } catch (error) {
    console.error('Failed to update metadata:', error);
    return false;
  }
};

/**
 * Check if user has valid cid (company ID)
 * @returns {boolean}
 */
export const hasValidCid = () => {
  const metadata = getMetadata();
  return metadata && metadata.cid != null && metadata.cid !== '';
};

/**
 * Get user ID from metadata
 * @returns {string|number|null}
 */
export const getUserId = () => {
  const metadata = getMetadata();
  return metadata?.uid || metadata?.userId || metadata?.id || null;
};

/**
 * Get company ID (cid) from metadata
 * @returns {string|number|null}
 */
export const getCid = () => {
  const metadata = getMetadata();
  return metadata?.cid || null;
};

export const clearAuthSession = () => {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('metadata');
  } catch (error) {
    console.error('Failed to clear auth session:', error);
  }
};

const encryptedStorage = {
  getAppSecretKey,
  setEncryptedItem,
  getEncryptedItem,
  removeEncryptedItem,
  getMetadata,
  getAuthToken,
  setMetadata,
  updateMetadata,
  hasValidCid,
  getUserId,
  getCid,
  clearAuthSession,
};

export default encryptedStorage;
